/**
 * Force-reconnect all live websockets when a suspended tab resumes.
 *
 * On mobile (screen-off then -on, app switch) and on desktop tab suspension the
 * underlying WebSocket can end up in a dead "zombie" state that still reports
 * readyState OPEN and never fires a `close`/`error` event. The
 * `ReconnectingWebSocket` wrapper only auto-reconnects on those events, so it
 * never notices the connection is gone: live data stays dead and the
 * "connection lost" banner stays up. Compounding this on mobile, the
 * hidden-tab close timer in `useVisibilityTabChange` is frozen while the tab is
 * suspended, so it never fires and the socket is never torn down and re-minted
 * on return either.
 *
 * On the hidden -> visible transition (and on a bfcache restore) we therefore
 * proactively force a reconnect of every live socket. `reconnect()` on a
 * `ReconnectingWebSocket` force-closes even a zombie (readyState OPEN) socket
 * and opens a fresh underlying connection, resetting the retry counter, so a
 * resumed tab reliably re-establishes its sockets.
 *
 * Installed once, globally: however many components mount the websocket hooks, a
 * single resume triggers exactly one (debounced) reconnect pass.
 */

let installed = false;
let lastTrigger = 0;

/** Reset the module-level singleton state. Test-only. */
export const _resetResumeReconnectForTests = () => {
    installed = false;
    lastTrigger = 0;
};

export const installResumeReconnect = (
    reconnectAll: () => void,
    doc: Document = typeof document !== "undefined" ? document : (undefined as any),
    win: Window = typeof window !== "undefined" ? window : (undefined as any)
) => {
    if (installed || !doc || !win) {
        return;
    }
    installed = true;

    let wasHidden = doc.visibilityState === "hidden";

    const trigger = () => {
        const now = Date.now();
        // Collapse a visibilitychange + pageshow that both fire on the same
        // resume into a single reconnect pass.
        if (now - lastTrigger < 1000) {
            return;
        }
        lastTrigger = now;
        reconnectAll();
    };

    doc.addEventListener("visibilitychange", () => {
        if (doc.visibilityState === "hidden") {
            wasHidden = true;
            return;
        }
        // Only reconnect on a genuine resume (we were hidden). Guards against a
        // spurious visible event without a preceding hidden one.
        if (wasHidden) {
            wasHidden = false;
            trigger();
        }
    });

    win.addEventListener("pageshow", (event) => {
        // Only a bfcache restore (persisted) needs a proactive reconnect; a
        // normal page load already opens fresh sockets.
        if ((event as PageTransitionEvent).persisted) {
            trigger();
        }
    });
};
